import { Link } from 'react-router-dom';
import type { ReactNode } from 'react';
import styles from './Card.module.css';

export interface CardProps {
  readonly title: ReactNode;
  /** Se presente, il titolo diventa un link e tutta la card è cliccabile. */
  readonly to?: string;
  readonly eyebrow?: ReactNode;
  readonly children?: ReactNode;
  readonly footer?: ReactNode;
  /**
   * Livello semantico dell'intestazione. Va scelto in base alla posizione
   * nella pagina, non all'estetica: saltare da h2 a h4 è una violazione
   * (WCAG 1.3.1). Default `h3`, che è il caso più comune in una griglia
   * dentro una sezione con h2.
   */
  readonly headingLevel?: 2 | 3 | 4 | 5;
  readonly className?: string;
}

export function Card({
  title,
  to,
  eyebrow,
  children,
  footer,
  headingLevel = 3,
  className,
}: CardProps) {
  const Heading = `h${headingLevel}` as 'h2' | 'h3' | 'h4' | 'h5';

  return (
    <div
      className={[styles.card, to ? styles.interactive : null, className]
        .filter(Boolean)
        .join(' ')}
    >
      {eyebrow ? <p className={styles.eyebrow}>{eyebrow}</p> : null}
      <Heading className={styles.title}>
        {to ? (
          <Link to={to} className={styles.titleLink}>
            {title}
          </Link>
        ) : (
          title
        )}
      </Heading>
      {children ? <div className={styles.body}>{children}</div> : null}
      {footer ? <div className={styles.footer}>{footer}</div> : null}
    </div>
  );
}

export interface CardGridProps {
  readonly children: ReactNode;
  /** Etichetta della lista. Una griglia di card È una lista: va detto. */
  readonly label: string;
  readonly className?: string;
}

/**
 * Griglia di card come lista semantica.
 * Un `<ul>` con `list-style:none` perde il ruolo `list` in Safari+VoiceOver:
 * `role="list"` esplicito lo rimette.
 */
export function CardGrid({ children, label, className }: CardGridProps) {
  return (
    <ul
      role="list"
      aria-label={label}
      className={[styles.grid, className].filter(Boolean).join(' ')}
    >
      {children}
    </ul>
  );
}
